import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setBrandHeader } from '../lib/api.js';
import { useAuth } from './useAuth.jsx';

const BrandContext = createContext(null);

/**
 * The brands this account runs, and which one the dashboard is showing.
 *
 * The choice lives on the server (`activeBrandId`) so it survives a reload
 * without browser storage, and is sent with every request (X-Affily-Brand) so
 * two tabs on two brands each act on their own. A `?brand=<id>` in the URL —
 * from an email, or Stripe sending the person back — switches to that brand.
 */
export function BrandProvider({ children }) {
  const { user } = useAuth();
  const isBrand = Boolean(user?.roles?.includes('brand'));
  const [state, setState] = useState({ loaded: false, brands: [], activeBrandId: null });

  /**
   * `select` is a brand to switch to once loaded: one just created. The list
   * alone can't say so — this tab still names the old brand on every request,
   * so the server answers with that one as current.
   */
  const load = useCallback(async (select) => {
    const data = await api.get('/brands/me/all');
    let active = data.brands.some((b) => b.id === select) ? select : data.activeBrandId;
    const wanted = new URLSearchParams(window.location.search).get('brand');
    if (wanted && wanted !== active && data.brands.some((b) => b.id === wanted)) {
      await api.put('/brands/me/active', { brandId: wanted });
      active = wanted;
    }
    setBrandHeader(active);
    setState({ loaded: true, brands: data.brands, activeBrandId: active });
  }, []);

  useEffect(() => {
    if (!isBrand) {
      setBrandHeader(null);
      setState({ loaded: true, brands: [], activeBrandId: null });
      return;
    }
    setState((s) => ({ ...s, loaded: false }));
    load().catch(() => setState({ loaded: true, brands: [], activeBrandId: null }));
  }, [user?.id, isBrand, load]);

  const switchBrand = useCallback(async (id) => {
    await api.put('/brands/me/active', { brandId: id });
    setBrandHeader(id);
    setState((s) => ({ ...s, activeBrandId: id }));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      activeBrand: state.brands.find((b) => b.id === state.activeBrandId) ?? null,
      switchBrand,
      /** Re-read the list; after creating a brand, pass its id to switch to it. */
      reload: load,
    }),
    [state, switchBrand, load],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used inside <BrandProvider>');
  return ctx;
}
